/**
 * Super Admin Contracts
 *
 * Zod schemas for Super Admin staff management operations.
 * Super Admin can create/deactivate admins, workers, and referees.
 */
import { z } from 'zod';
// =============================================================================
// ENUMS
// =============================================================================
/**
 * Staff roles that can be managed by Super Admin
 * Note: super_admin cannot be created via UI - only via ENV bootstrap
 */
export const superadminManagedRoles = ['admin', 'worker', 'referee'];
// =============================================================================
// REQUEST SCHEMAS
// =============================================================================
/**
 * Create staff member request
 */
export const superadminCreateStaffRequestSchema = z.object({
    telegramId: z.string().regex(/^\d+$/, 'Must be a valid Telegram ID'),
    role: z.enum(superadminManagedRoles),
    // Only required for referee
    name: z.string().max(100).optional(),
    referralCode: z.string().min(3).max(20).regex(/^[A-Za-z0-9_-]+$/).optional(),
});
/**
 * Deactivate staff member request
 */
export const superadminDeactivateStaffRequestSchema = z.object({
    identifier: z.string().min(1).max(16),
});
/**
 * Reactivate staff member request
 */
export const superadminReactivateStaffRequestSchema = z.object({
    identifier: z.string().min(1).max(16),
});
// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================
/**
 * Staff member in list response (for superadmin panel)
 */
export const superadminStaffMemberSchema = z.object({
    identifier: z.string(),
    role: z.enum(['super_admin', 'admin', 'worker', 'referee']),
    active: z.boolean(),
    createdAt: z.string(),
    lastActiveDate: z.string().nullable(),
    // Only for referees
    name: z.string().nullable().optional(),
    referralCode: z.string().nullable().optional(),
    balance: z.number().nullable().optional(),
});
/**
 * List all staff response
 */
export const superadminListStaffResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        staff: z.array(superadminStaffMemberSchema),
        total: z.number(),
    }),
});
/**
 * Create staff response
 */
export const superadminCreateStaffResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        identifier: z.string(),
        role: z.enum(['super_admin', 'admin', 'worker', 'referee']),
        referralCode: z.string().optional(),
        message: z.string(),
    }),
});
/**
 * Deactivate/Reactivate staff response
 */
export const superadminToggleStaffResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        identifier: z.string(),
        active: z.boolean(),
        message: z.string(),
    }),
});
/**
 * Error response
 */
export const superadminErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
//# sourceMappingURL=index.js.map